import React, { useState, useEffect, useRef, useCallback } from 'react';
import useModalBackButton from './hooks/useModalBackButton';
import { HashRouter as Router, Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { loadTimezone } from './utils/timezone';
import { loadDryCleaningEnabled } from './utils/featureFlags';
import { APP_BUILD_TIME, APP_VERSION } from './generated/appVersion';
import {
  Wifi,
  WifiOff,
  AlertTriangle,
  Plus,
  ListTodo,
  FileText,
  BookOpen,
  Newspaper,
  Route as RouteIcon,
  DollarSign,
  BarChart3,
  Menu,
  X,
  LogOut,
  Bell,
  Settings as SettingsIcon,
  Shield,
  ShieldAlert
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import Header from './components/Header';
import ModalShell from './components/ModalShell';
import AppErrorBoundary from './components/AppErrorBoundary';
import AppStatusScreen from './components/AppStatusScreen';
import PWAInstallButton from './components/PWAInstallButton';
import ScrollToTop from './components/ScrollToTop';
import { resetRouteScrollPosition } from './components/ScrollToTop';
import { saveRouteScrollPosition } from './utils/routeScrollMemory';
import NewOrder from './pages/NewOrder';
import Profile from './pages/Profile';
import MyOrders from './pages/MyOrders';
import OrderLog from './pages/OrderLog';
import OrderDetail from './pages/OrderDetail';
import OrderCreatedSummaryPage from './pages/OrderCreatedSummaryPage';
import Settings from './pages/Settings';
import UserAgreementPage from './pages/UserAgreement';
import News from './pages/News';
import Management from './pages/Management';
import UsersManagement from './pages/management/UsersManagement';
import DepartmentsManagement from './pages/management/DepartmentsManagement';
import ServicesManagement from './pages/management/ServicesManagement';
import ClientAppManagement from './pages/management/ClientAppManagement';
import ActivityLog from './pages/management/ActivityLog';
import DiscountsManagement from './pages/management/DiscountsManagement';
import NotificationsManagement from './pages/management/NotificationsManagement';
import NewsManagement from './pages/management/NewsManagement';
import SystemManagement from './pages/management/SystemManagement';
import SecurityManagement from './pages/management/SecurityManagement';
import ClientsManagement from './pages/management/ClientsManagement';
import ProfitRadar from './pages/management/ProfitRadar';
import Expenses from './pages/Expenses';
import ExpensesAnalytics from './pages/ExpensesAnalytics';
import Statistics from './pages/Statistics';
import ResetPassword from './pages/ResetPassword';
import RoutesPage from './pages/RoutesPage';
import TasksStagePage from './pages/TasksStagePage';
import NewTaskPage from './pages/NewTaskPage';
import TaskDetail from './pages/TaskDetail';
import TodayDashboard from './pages/TodayDashboard';
import Auth from './pages/Auth';
import { API_URL } from './config';
import { clearRoutesNavigationContext } from './utils/navigationContext';
import {
  clearAllOfflineRoutesCache,
  saveOfflineMyOrdersProfile,
  readOfflineRoutesSnapshot
} from './utils/offlineRoutesCache';
import { safeFetch } from './utils/safeFetch';
import { OfflineProvider } from './contexts/OfflineContext';
import { OfflineModal } from './components/OfflineControls';
import { haptic } from './utils/vibration';
import { StageProvider, useStageContext } from './contexts/StageContext';
import { useInteractionPreferences } from './contexts/InteractionPreferencesContext';
import NewsStartupModal from './components/NewsStartupModal';
import {
  NewYearSidebarBrand,
  NewYearAvatarSantaHat,
  NewYearTreeButton,
  NewYearMusicSnowfall
} from './components/NewYearSidebarDecorations';
import { getNewsUnreadCount, NEWS_STATE_CHANGED_EVENT } from './services/newsApi';
import UserAgreementGate from './components/UserAgreementGate';
import { AppShellSkeleton } from './components/Skeleton';
import { getStagesFromRoles } from './utils/stages';
import { USER_AGREEMENT_VERSION } from './legal/userAgreement';
import {
  NEW_YEAR_PREVIEW_STORAGE_KEY,
  NEW_YEAR_DISABLED_STORAGE_KEY,
  NEW_YEAR_PREVIEW_CHANGED_EVENT,
  isNewYearInterfaceEnabled,
  shouldShowNewYearInterface
} from './utils/newYearInterface';

const APP_VISUAL_VIEWPORT_BOTTOM_INSET_VAR = '--app-visual-viewport-bottom-inset';
const APP_BOTTOM_SAFE_OFFSET = `max(env(safe-area-inset-bottom, 0px), var(${APP_VISUAL_VIEWPORT_BOTTOM_INSET_VAR}, 0px))`;
const APP_BOTTOM_SAFE_PADDING = `calc(${APP_BOTTOM_SAFE_OFFSET} + 0.75rem)`;
const APP_MIN_CONTENT_BOTTOM_CLEARANCE = '4rem';
const NETWORK_HEALTHCHECK_TIMEOUT_MS = 8000;
const NETWORK_HEALTHCHECK_INTERVAL_MS = 15000;
const NETWORK_RESTORED_BANNER_MS = 3000;
const AUTH_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const AVAILABLE_MENUS_CACHE_KEY = 'available_menus';
const CURRENT_USER_SNAPSHOT_CACHE_KEY = 'current_user_snapshot';
const DEFAULT_CURRENT_USER = {
  name: 'Загрузка...',
  role: 'user',
  avatar: '?'
};
const DEFAULT_USER_AGREEMENT_STATE = {
  accepted: null,
  version: '',
  requiredVersion: USER_AGREEMENT_VERSION,
  acceptedAt: null
};

const resolveUserAgreementState = (data = {}) => {
  const accepted = typeof data.user_agreement_accepted === 'boolean'
    ? data.user_agreement_accepted
    : null;

  return {
    accepted,
    version: data.user_agreement_version || '',
    requiredVersion: data.user_agreement_required_version || USER_AGREEMENT_VERSION,
    acceptedAt: data.user_agreement_accepted_at || null
  };
};

const readCachedAvailableMenus = () => {
  if (typeof localStorage === 'undefined') return [];

  try {
    const parsed = JSON.parse(localStorage.getItem(AVAILABLE_MENUS_CACHE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Не удалось прочитать кеш доступных меню:', error);
    return [];
  }
};

const writeCachedAvailableMenus = (menus) => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(AVAILABLE_MENUS_CACHE_KEY, JSON.stringify(Array.isArray(menus) ? menus : []));
};

const readCachedCurrentUser = () => {
  if (typeof localStorage === 'undefined') return DEFAULT_CURRENT_USER;

  try {
    const parsed = JSON.parse(localStorage.getItem(CURRENT_USER_SNAPSHOT_CACHE_KEY) || 'null');
    if (!parsed || typeof parsed !== 'object') return DEFAULT_CURRENT_USER;
    return {
      ...DEFAULT_CURRENT_USER,
      ...parsed,
      role: parsed.role || localStorage.getItem('user_type') || DEFAULT_CURRENT_USER.role
    };
  } catch (error) {
    console.warn('Не удалось прочитать кеш пользователя:', error);
    return DEFAULT_CURRENT_USER;
  }
};

const writeCachedCurrentUser = (user) => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(CURRENT_USER_SNAPSHOT_CACHE_KEY, JSON.stringify({
    name: user?.name || DEFAULT_CURRENT_USER.name,
    role: user?.role || DEFAULT_CURRENT_USER.role,
    avatar: user?.avatar || DEFAULT_CURRENT_USER.avatar,
    first_name: user?.first_name || '',
    last_name: user?.last_name || ''
  }));
};

const clearCachedAuthPresentation = () => {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(AVAILABLE_MENUS_CACHE_KEY);
  localStorage.removeItem(CURRENT_USER_SNAPSHOT_CACHE_KEY);
};

const resolveCurrentUserSnapshot = (data) => ({
  name: `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Пользователь',
  role: data.user_type,
  avatar: (data.first_name?.[0] || 'U').toUpperCase(),
  first_name: data.first_name,
  last_name: data.last_name
});

const shouldInvalidateSessionAfterAuthCheckError = (error) => {
  const message = String(error?.message || error || '').trim().toLowerCase();
  return message.includes('сессия истекла')
    || message.includes('требуется авторизация')
    || message.includes('доступ запрещ')
    || message.includes('заблокирован')
    || message.includes('удален')
    || message.includes('удалён')
    || message.includes('user_blocked')
    || message.includes('user_deleted');
};

const isPwaUpdatePreviewEnabled = () => {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return false;
  const urlText = `${window.location.search || ''}${window.location.hash || ''}`;
  return /(?:[?&#])pwa-update-preview(?:=1|=true|&|$)/i.test(urlText);
};

// Обёртка для OrderDetail с роутингом
const OrderDetailWrapper = ({ canAccessOrdersLog = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const requestedSource = searchParams.get('from') || '';
  const source = requestedSource === 'orders-log' && !canAccessOrdersLog ? '' : requestedSource;
  const forceLimitedArchiveAccess = source === 'my-orders' && searchParams.get('tab') === 'archive';
  return (
    <OrderDetail
      orderId={id}
      onBack={() => navigate(-1)}
      onNavigate={navigate}
      accessSource={source}
      forceLimitedArchiveAccess={forceLimitedArchiveAccess}
    />
  );
};

// Регистрация Service Worker для PWA и Push уведомлений
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Версия в URL не дает Safari/PWA оставить старый sw.js в HTTP-кэше.
    navigator.serviceWorker.register(`/sw.js?v=${encodeURIComponent(APP_BUILD_TIME || APP_VERSION)}`, { scope: '/' })
      .then((registration) => {
        console.log('Service Worker зарегистрирован успешно:', registration.scope);
      })
      .catch((error) => {
        console.error('Ошибка регистрации Service Worker:', error);
      });
  });
}

const userRoles = {
  admin: "Администратор",
  dispatcher: "Диспетчер", 
  user: "Пользователь",
  developer: "Разработчик"
};

const menuItems = [
  { id: "new-order", label: "Новый заказ", icon: Plus, roles: ["admin", "dispatcher", "developer", "user"], path: "/new-order" },
  { id: "my-orders", label: "Мои заказы", icon: FileText, roles: ["admin", "dispatcher", "developer", "user"], path: "/my-orders" },
  { id: "tasks", label: "Поручения", icon: ListTodo, roles: ["admin", "dispatcher", "developer", "user"], path: "/tasks" },
  { id: "orders-log", label: "Журнал заказов", icon: BookOpen, roles: ["admin", "dispatcher", "developer"], path: "/orders-log" },
  { id: "risk-control", label: "Контроль рисков", icon: ShieldAlert, roles: ["admin", "dispatcher", "developer"], path: "/risk-control" },
  { id: "routes", label: "Маршруты", icon: RouteIcon, roles: ["admin", "dispatcher", "developer", "user"], path: "/routes" },
  { id: "expenses", label: "Расходы", icon: DollarSign, roles: ["admin", "dispatcher", "developer", "user"], path: "/expenses" },
  { id: "statistics", label: "Статистика", icon: BarChart3, roles: ["admin", "dispatcher", "developer", "user"], path: "/statistics" },
  { id: "management", label: "Управление", icon: Shield, roles: ["admin", "developer"], path: "/management" },
  { id: "news", label: "Новости", icon: Newspaper, roles: ["admin", "dispatcher", "developer", "user"], path: "/news" },
  { id: "settings", label: "Настройки", icon: SettingsIcon, roles: ["admin", "dispatcher", "developer", "user"], path: "/settings" }
];

const canAccessMenuItem = (item, userType) => item.roles.includes(userType);

const SYSTEM_THEME_PALETTES = {
  light: {
    background: '#f8fafc',
    surface: '#ffffff',
    appleStatusBarStyle: 'default'
  },
  dark: {
    background: '#0f172a',
    surface: '#111827',
    appleStatusBarStyle: 'black-translucent'
  }
};

const applySystemThemePalette = (theme) => {
  if (typeof document === 'undefined') return;

  const nextTheme = theme === 'dark' ? 'dark' : 'light';
  const palette = SYSTEM_THEME_PALETTES[nextTheme];
  const root = document.documentElement;

  root.setAttribute('data-theme', nextTheme);
  root.style.backgroundColor = palette.background;
  root.style.colorScheme = nextTheme;

  if (document.body) {
    document.body.style.backgroundColor = palette.background;
    document.body.style.colorScheme = nextTheme;
  }

  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', palette.surface);
  }

  const appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  if (appleStatusBarMeta) {
    appleStatusBarMeta.setAttribute('content', palette.appleStatusBarStyle);
  }
};

const NetworkStatusBanner = ({ status, onClose = null }) => {
  if (!status) return null;

  const bannerMeta = {
    offline: {
      icon: WifiOff,
      className: 'border-red-200 bg-red-50/95 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300',
      message: 'Нет интернета. Проверьте соединение.'
    },
    server_unavailable: {
      icon: AlertTriangle,
      className: 'border-amber-200 bg-amber-50/95 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
      message: 'Сервер недоступен. Пытаюсь восстановить связь.'
    },
    restored: {
      icon: Wifi,
      className: 'border-emerald-200 bg-emerald-50/95 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/35 dark:text-emerald-300',
      message: 'Связь с сервером восстановлена.'
    }
  };
  const meta = bannerMeta[status] || bannerMeta.restored;
  const Icon = meta.icon;

  return (
    <AnimatePresence>
      <motion.div
        key={status}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.18 }}
        className={`relative flex min-h-10 items-center gap-2 border-t px-4 py-2 pr-10 text-xs font-medium ${meta.className}`}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="truncate">
          {meta.message}
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md transition-colors hover:bg-black/5 dark:hover:bg-white/10"
            aria-label="Закрыть уведомление о сети"
            title="Закрыть"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

const NotFoundRouteScreen = ({ authenticated = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentRoute = `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;

  return (
    <AppStatusScreen
      mode="notFound"
      title="Не удалось открыть раздел"
      description={authenticated
        ? 'Такого экрана в приложении сейчас нет. Возможно, ссылка устарела или раздел был перенесён.'
        : 'Ссылка не найдена. Возможно, адрес устарел или страница больше недоступна.'}
      details={`Маршрут: ${currentRoute}`}
      actions={authenticated ? [
        {
          kind: 'home',
          label: 'На главную',
          onClick: () => navigate('/my-orders', { replace: true })
        },
        {
          kind: 'reload',
          label: 'Обновить',
          tone: 'secondary',
          onClick: () => window.location.reload()
        }
      ] : [
        {
          kind: 'home',
          label: 'К авторизации',
          onClick: () => navigate('/auth', { replace: true })
        }
      ]}
    />
  );
};

function RouteScrollContainer({ children }) {
  const location = useLocation();
  const pendingScrollTopRef = useRef(null);
  const scrollSaveFrameRef = useRef(null);

  const flushScrollPosition = useCallback(() => {
    if (pendingScrollTopRef.current === null) return;
    saveRouteScrollPosition(location, pendingScrollTopRef.current);
    pendingScrollTopRef.current = null;
  }, [location]);

  const handleRouteScroll = useCallback((event) => {
    pendingScrollTopRef.current = Number(event.currentTarget?.scrollTop || 0);
    if (scrollSaveFrameRef.current !== null) return;

    scrollSaveFrameRef.current = window.requestAnimationFrame(() => {
      scrollSaveFrameRef.current = null;
      flushScrollPosition();
    });
  }, [flushScrollPosition]);

  useEffect(() => () => {
    if (scrollSaveFrameRef.current !== null) {
      window.cancelAnimationFrame(scrollSaveFrameRef.current);
      scrollSaveFrameRef.current = null;
    }
    flushScrollPosition();
  }, [flushScrollPosition]);

  return (
    <div
      key={location.pathname}
      data-route-scroll-container="true"
      onScroll={handleRouteScroll}
      className="main-scrollable min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-4"
      style={{
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        paddingBottom: `max(${APP_MIN_CONTENT_BOTTOM_CLEARANCE}, ${APP_BOTTOM_SAFE_PADDING})`
      }}
    >
      {children}
    </div>
  );
}

function App() {
  const { animationsEnabled } = useInteractionPreferences();
  const [currentUser, setCurrentUser] = useState(() => readCachedCurrentUser());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [newYearClock, setNewYearClock] = useState(() => Date.now());
  const [newYearActive, setNewYearActive] = useState(() => (
    isNewYearInterfaceEnabled({
      date: new Date(),
      userType: localStorage.getItem('user_type') || ''
    })
  ));
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [userType, setUserType] = useState(() => localStorage.getItem('user_type'));
  const [availableMenus, setAvailableMenus] = useState(() => readCachedAvailableMenus());
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [loading, setLoading] = useState(() => !!localStorage.getItem('token'));
  const [userAgreement, setUserAgreement] = useState(DEFAULT_USER_AGREEMENT_STATE);
  const [networkBannerStatus, setNetworkBannerStatus] = useState(() => (
    typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : ''
  ));
  const [dismissedNetworkBannerStatus, setDismissedNetworkBannerStatus] = useState('');
  const networkBannerTimerRef = useRef(null);
  const networkHealthcheckTimerRef = useRef(null);
  const networkHealthcheckAbortRef = useRef(null);
  const networkHealthcheckInFlightRef = useRef(false);
  const hadNetworkIssueRef = useRef(typeof navigator !== 'undefined' && navigator.onLine === false);
  const visibleNetworkBannerStatus = (
    networkBannerStatus && dismissedNetworkBannerStatus !== networkBannerStatus
      ? networkBannerStatus
      : ''
  );
  const userAgreementAllowsAppEffects = userAgreement.accepted !== false;
  const userAgreementRequiresAction = Boolean(token && !loading && userAgreement.accepted === false);

  useEffect(() => {
    if (!networkBannerStatus) {
      setDismissedNetworkBannerStatus('');
      return;
    }

    setDismissedNetworkBannerStatus((prev) => (
      prev && prev !== networkBannerStatus ? '' : prev
    ));
  }, [networkBannerStatus]);

  const handleDismissNetworkBanner = () => {
    if (!networkBannerStatus) return;
    setDismissedNetworkBannerStatus(networkBannerStatus);
  };

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    applySystemThemePalette(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNewYearClock(Date.now());
    }, 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const syncState = () => {
      setNewYearActive(
        isNewYearInterfaceEnabled({
          date: new Date(newYearClock),
          userType
        })
      );
    };

    const handlePreviewChange = (event) => {
      if (typeof event?.detail === 'boolean') {
        setNewYearActive(event.detail);
      } else {
        syncState();
      }
    };

    const handleStorage = (event) => {
      if (
        event.key === NEW_YEAR_PREVIEW_STORAGE_KEY
        || event.key === NEW_YEAR_DISABLED_STORAGE_KEY
      ) {
        syncState();
      }
    };

    window.addEventListener(NEW_YEAR_PREVIEW_CHANGED_EVENT, handlePreviewChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(NEW_YEAR_PREVIEW_CHANGED_EVENT, handlePreviewChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [newYearClock, userType]);

  useEffect(() => {
    let disposed = false;

    const clearRestoreTimer = () => {
      if (networkBannerTimerRef.current) {
        clearTimeout(networkBannerTimerRef.current);
        networkBannerTimerRef.current = null;
      }
    };

    const clearHealthcheckTimer = () => {
      if (networkHealthcheckTimerRef.current) {
        clearInterval(networkHealthcheckTimerRef.current);
        networkHealthcheckTimerRef.current = null;
      }
    };

    const cancelHealthcheckRequest = () => {
      if (networkHealthcheckAbortRef.current) {
        networkHealthcheckAbortRef.current.abort();
        networkHealthcheckAbortRef.current = null;
      }
      networkHealthcheckInFlightRef.current = false;
    };

    const markOffline = () => {
      if (disposed) return;
      clearRestoreTimer();
      hadNetworkIssueRef.current = true;
      setNetworkBannerStatus('offline');
    };

    const markServerUnavailable = () => {
      if (disposed) return;
      clearRestoreTimer();
      hadNetworkIssueRef.current = true;
      setNetworkBannerStatus('server_unavailable');
    };

    const markServerReachable = () => {
      if (disposed) return;
      clearRestoreTimer();
      if (hadNetworkIssueRef.current) {
        setNetworkBannerStatus('restored');
        networkBannerTimerRef.current = setTimeout(() => {
          if (disposed) return;
          setNetworkBannerStatus('');
          networkBannerTimerRef.current = null;
        }, NETWORK_RESTORED_BANNER_MS);
      } else {
        setNetworkBannerStatus('');
      }
      hadNetworkIssueRef.current = false;
    };

    const runHealthCheck = async () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        markOffline();
        return;
      }

      if (networkHealthcheckInFlightRef.current) {
        return;
      }

      networkHealthcheckInFlightRef.current = true;
      const controller = new AbortController();
      networkHealthcheckAbortRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), NETWORK_HEALTHCHECK_TIMEOUT_MS);

      try {
        const response = await fetch(`${API_URL}/api/health`, {
          method: 'GET',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Health check failed: ${response.status}`);
        }

        markServerReachable();
      } catch (error) {
        clearTimeout(timeoutId);
        if (controller.signal.aborted && typeof navigator !== 'undefined' && navigator.onLine === false) {
          markOffline();
        } else {
          markServerUnavailable();
        }
      } finally {
        if (networkHealthcheckAbortRef.current === controller) {
          networkHealthcheckAbortRef.current = null;
        }
        networkHealthcheckInFlightRef.current = false;
      }
    };

    let onlineRetryTimers = [];
    const clearOnlineRetryTimers = () => {
      onlineRetryTimers.forEach(clearTimeout);
      onlineRetryTimers = [];
    };

    const handleOffline = () => {
      clearOnlineRetryTimers();
      cancelHealthcheckRequest();
      markOffline();
    };

    const handleOnline = () => {
      clearOnlineRetryTimers();
      runHealthCheck();
      onlineRetryTimers.push(setTimeout(() => { if (!disposed) runHealthCheck(); }, 1500));
      onlineRetryTimers.push(setTimeout(() => { if (!disposed) runHealthCheck(); }, 4000));
    };

    const handleServerReachableEvent = () => {
      markServerReachable();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        markOffline();
        return;
      }
      runHealthCheck();
    };

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      markOffline();
    } else {
      runHealthCheck();
    }

    networkHealthcheckTimerRef.current = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        markOffline();
        return;
      }
      runHealthCheck();
    }, NETWORK_HEALTHCHECK_INTERVAL_MS);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('network-server-reachable', handleServerReachableEvent);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      clearRestoreTimer();
      clearHealthcheckTimer();
      clearOnlineRetryTimers();
      cancelHealthcheckRequest();
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('network-server-reachable', handleServerReachableEvent);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    // ОТКЛЮЧЕНО ПО ЗАПРОСУ ПОЛЬЗОВАТЕЛЯ
    // if (window.Telegram && window.Telegram.WebApp) {
    //   window.Telegram.WebApp.expand();
    // }
    
    // Загружаем часовой пояс с сервера
    if (token && userAgreementAllowsAppEffects) {
      loadTimezone();
      loadDryCleaningEnabled(token);
    }
  }, [token, userAgreementAllowsAppEffects]);

  // Обработчик событий ошибки авторизации
  useEffect(() => {
    const handleAuthError = (event) => {
      console.log('Получено событие auth-error, очищаем состояние');
      const isBlocked = event?.detail?.type === 'user_blocked'
        || /заблокирован|удалён|удален/i.test(String(event?.detail?.message || ''));
      sessionStorage.setItem('auth_notice', isBlocked ? 'user-blocked' : 'session-ended');
      localStorage.removeItem('token');
      localStorage.removeItem('user_type');
      localStorage.removeItem('user_id');
      clearCachedAuthPresentation();
      clearAllOfflineRoutesCache().catch((error) => {
        console.warn('Не удалось очистить офлайн-кэш маршрутов:', error);
      });
      setToken(null);
      setUserType(null);
      setAvailableMenus([]);
      setCurrentUser(DEFAULT_CURRENT_USER);
      setUserAgreement(DEFAULT_USER_AGREEMENT_STATE);
      window.location.hash = '#/auth';
    };

    const handleOfflineCacheExpired = () => {
      console.log('Получено событие offline-cache-expired, сессия завершена по TTL');
      sessionStorage.setItem('auth_notice', 'offline-expired');
      localStorage.removeItem('token');
      localStorage.removeItem('user_type');
      localStorage.removeItem('user_id');
      clearCachedAuthPresentation();
      clearAllOfflineRoutesCache().catch((error) => {
        console.warn('Не удалось очистить офлайн-кэш маршрутов:', error);
      });
      setToken(null);
      setUserType(null);
      setAvailableMenus([]);
      setCurrentUser(DEFAULT_CURRENT_USER);
      setUserAgreement(DEFAULT_USER_AGREEMENT_STATE);
      window.location.hash = '#/auth';
    };

    window.addEventListener('auth-error', handleAuthError);
    window.addEventListener('offline-cache-expired', handleOfflineCacheExpired);
    return () => {
      window.removeEventListener('auth-error', handleAuthError);
      window.removeEventListener('offline-cache-expired', handleOfflineCacheExpired);
    };
  }, []);

  useEffect(() => {
    const handleAuthRefresh = () => {
      if (localStorage.getItem('token')) {
        checkAuth();
      }
    };

    window.addEventListener('auth-refresh', handleAuthRefresh);
    return () => {
      window.removeEventListener('auth-refresh', handleAuthRefresh);
    };
  }, [token]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const root = document.documentElement;
    const visualViewport = window.visualViewport;

    const updateViewportBottomInset = () => {
      const layoutViewportHeight = Math.max(window.innerHeight || 0, root.clientHeight || 0);
      const visualViewportBottom = visualViewport
        ? (visualViewport.height + visualViewport.offsetTop)
        : layoutViewportHeight;
      const rawBottomInset = Math.max(0, Math.round(layoutViewportHeight - visualViewportBottom));

      // Большой inset обычно означает открытую клавиатуру, а не постоянную нижнюю панель браузера.
      const normalizedBottomInset = rawBottomInset > 160 ? 0 : rawBottomInset;
      root.style.setProperty(APP_VISUAL_VIEWPORT_BOTTOM_INSET_VAR, `${normalizedBottomInset}px`);
    };

    updateViewportBottomInset();

    window.addEventListener('resize', updateViewportBottomInset);
    window.addEventListener('orientationchange', updateViewportBottomInset);
    visualViewport?.addEventListener('resize', updateViewportBottomInset);
    visualViewport?.addEventListener('scroll', updateViewportBottomInset);

    return () => {
      window.removeEventListener('resize', updateViewportBottomInset);
      window.removeEventListener('orientationchange', updateViewportBottomInset);
      visualViewport?.removeEventListener('resize', updateViewportBottomInset);
      visualViewport?.removeEventListener('scroll', updateViewportBottomInset);
      root.style.removeProperty(APP_VISUAL_VIEWPORT_BOTTOM_INSET_VAR);
    };
  }, []);

  // Проверка авторизации при загрузке
  useEffect(() => {
    if (token) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        readOfflineRoutesSnapshot(null, { allowExpired: false }).catch(() => null);
        setLoading(false);
        const handleOnlineAuthCheck = () => {
          checkAuth();
        };
        window.addEventListener('online', handleOnlineAuthCheck, { once: true });
        return () => {
          window.removeEventListener('online', handleOnlineAuthCheck);
        };
      }
      checkAuth();
    }
  }, [token]);

  async function checkAuth({ background = false } = {}) {
    if (!background) {
      setLoading(true);
    }
    try {
      const data = await safeFetch(`${API_URL}/api/check-auth`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const nextCurrentUser = resolveCurrentUserSnapshot(data);
      setCurrentUser(nextCurrentUser);
      writeCachedCurrentUser(nextCurrentUser);
      setUserAgreement(resolveUserAgreementState(data));
      setUserType(data.user_type);
      localStorage.setItem('user_type', data.user_type);
      if (typeof data.user_roles !== 'undefined') {
        localStorage.setItem('user_roles', typeof data.user_roles === 'string' ? data.user_roles : JSON.stringify(data.user_roles || []));
      }
      if (data.user_id) {
        localStorage.setItem('user_id', String(data.user_id));
        saveOfflineMyOrdersProfile({
          userId: data.user_id,
          stageRoles: getStagesFromRoles(data.user_roles || '[]')
        }).catch((cacheError) => {
          console.warn('Не удалось сохранить офлайн-профиль моих заказов:', cacheError);
        });
      }

      // Парсим доступные меню из JSON строки
      try {
        const menus = JSON.parse(data.available_menus || '[]');
        setAvailableMenus(menus);
        writeCachedAvailableMenus(menus);
      } catch (e) {
        console.error('Ошибка парсинга меню:', e);
        setAvailableMenus([]);
        writeCachedAvailableMenus([]);
      }
    } catch (error) {
      console.error('Ошибка проверки авторизации:', error);
      if (shouldInvalidateSessionAfterAuthCheckError(error)) {
        const errorMsg = String(error?.message || error || '').toLowerCase();
        const isBlocked = errorMsg.includes('заблокирован') || errorMsg.includes('удален') || errorMsg.includes('удалён');
        sessionStorage.setItem('auth_notice', isBlocked ? 'user-blocked' : 'session-ended');
        localStorage.removeItem('token');
        localStorage.removeItem('user_type');
        localStorage.removeItem('user_id');
        clearCachedAuthPresentation();
        clearAllOfflineRoutesCache().catch((cacheError) => {
          console.warn('Не удалось очистить офлайн-кэш маршрутов:', cacheError);
        });
        setToken(null);
        setUserType(null);
        setAvailableMenus([]);
        setCurrentUser(DEFAULT_CURRENT_USER);
        setUserAgreement(DEFAULT_USER_AGREEMENT_STATE);
        window.location.hash = '#/auth';
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (!token) return undefined;

    const runBackgroundAuthCheck = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      checkAuth({ background: true });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        runBackgroundAuthCheck();
      }
    };

    const intervalId = window.setInterval(runBackgroundAuthCheck, AUTH_CHECK_INTERVAL_MS);
    window.addEventListener('focus', runBackgroundAuthCheck);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', runBackgroundAuthCheck);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token]);

  // Обработка успешной авторизации
  const handleAuth = (data) => {
    const previousUserId = localStorage.getItem('user_id');
    if (previousUserId && data.user_id && String(previousUserId) !== String(data.user_id)) {
      clearAllOfflineRoutesCache().catch((cacheError) => {
        console.warn('Не удалось очистить кэш предыдущего пользователя:', cacheError);
      });
    }
    setToken(data.access_token);
    setUserType(data.user_type);
    setUserAgreement(resolveUserAgreementState(data));
    setIsLoggingOut(false);
    const nextCurrentUser = {
      name: 'Пользователь',
      role: data.user_type,
      avatar: 'U'
    };
    setCurrentUser(nextCurrentUser);
    writeCachedCurrentUser(nextCurrentUser);
    localStorage.setItem('user_type', data.user_type);
    if (typeof data.user_roles !== 'undefined') {
      localStorage.setItem('user_roles', typeof data.user_roles === 'string' ? data.user_roles : JSON.stringify(data.user_roles || []));
    }
    if (data.user_id) {
      localStorage.setItem('user_id', String(data.user_id));
      if (typeof data.user_roles !== 'undefined') {
        saveOfflineMyOrdersProfile({
          userId: data.user_id,
          stageRoles: getStagesFromRoles(data.user_roles || '[]')
        }).catch((cacheError) => {
          console.warn('Не удалось сохранить офлайн-профиль моих заказов:', cacheError);
        });
      }
    }
    
    // Парсим доступные меню
    try {
      const menus = JSON.parse(data.available_menus || '[]');
      setAvailableMenus(menus);
      writeCachedAvailableMenus(menus);
    } catch (e) {
      console.error('Ошибка парсинга меню при авторизации:', e);
      setAvailableMenus([]);
      writeCachedAvailableMenus([]);
    }
  };

  const handleUserAgreementAccepted = (status) => {
    setUserAgreement(resolveUserAgreementState(status));
  };

  // Выход
  const handleLogout = () => {
    const activeToken = localStorage.getItem('token');
    if (activeToken) {
      fetch(`${API_URL}/api/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${activeToken}`,
        },
        keepalive: true,
      }).catch((error) => {
        console.warn('Не удалось зафиксировать выход в журнале активности:', error);
      });
    }
    setIsLoggingOut(true);
    setLoading(false);
    setSidebarOpen(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user_type');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_roles');
    sessionStorage.removeItem('auth_notice');
    clearCachedAuthPresentation();
    clearAllOfflineRoutesCache().catch((error) => {
      console.warn('Не удалось очистить офлайн-кэш маршрутов:', error);
    });
    setToken(null);
    setUserType(null);
    setAvailableMenus([]);
    setUserAgreement(DEFAULT_USER_AGREEMENT_STATE);
    clearRoutesNavigationContext();
    setCurrentUser(DEFAULT_CURRENT_USER);
    window.location.hash = '#/auth';
  };

  // Фильтрация меню по доступным пунктам из базы данных
  const filteredMenuItems = menuItems.filter(item => availableMenus.includes(item.id) && canAccessMenuItem(item, userType));
  const canAccessTasks = availableMenus.includes('tasks');
  const canAccessOrdersLog = availableMenus.includes('orders-log') && ['admin', 'dispatcher', 'developer'].includes(userType);
  const canAccessNews = availableMenus.includes('news');
  const normalizedUserType = String(userType || '').trim().toLowerCase();
  const canAccessRiskControl = availableMenus.includes('risk-control')
    && ['admin', 'dispatcher', 'developer'].includes(normalizedUserType);
  const pwaUpdatePreviewEnabled = isPwaUpdatePreviewEnabled();
  const newYearInterfaceEnabled = shouldShowNewYearInterface({
    date: new Date(newYearClock),
    userType: normalizedUserType,
    previewEnabled: newYearActive,
    seasonDisabled: !newYearActive
  });

  if (loading) {
    return <AppShellSkeleton />;
  }

  if (userAgreementRequiresAction) {
    return (
      <UserAgreementGate
        onAccepted={handleUserAgreementAccepted}
        onLogout={handleLogout}
      />
    );
  }

  // Если нет токена и не загружается - показываем авторизацию
  if (!token && !loading) {
    return (
      <Router>
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
          <ScrollToTop />
          <NetworkStatusBanner status={visibleNetworkBannerStatus} onClose={handleDismissNetworkBanner} />
          <AppErrorBoundary resetKey={window.location.hash}>
            <Routes>
              <Route path="/auth" element={<Auth onAuth={handleAuth} isLoggingOut={isLoggingOut} />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<Navigate to="/auth" replace />} />
            </Routes>
          </AppErrorBoundary>
          <NewsStartupModal enabled={pwaUpdatePreviewEnabled} previewMode={pwaUpdatePreviewEnabled} />
        </div>
      </Router>
    );
  }

  // Если есть токен и не загружается - показываем основное приложение
  if (token && !loading) {
    return (
      <StageProvider>
        <Router>
          <OfflineProvider key={localStorage.getItem('user_id') || 'pending'} token={token} userId={Number(localStorage.getItem('user_id')) || null} userType={currentUser?.role || localStorage.getItem('user_type') || 'user'} networkStatus={networkBannerStatus}>
          <ScrollToTop />
          <NewYearMusicSnowfall enabled={newYearInterfaceEnabled} />
          <div
            className="flex h-screen min-h-screen max-h-screen flex-col overflow-hidden bg-gray-50 dark:bg-slate-900"
            style={{ height: '100dvh', minHeight: '100dvh', maxHeight: '100dvh' }}
          >
            {/* Header */}
            <Header setSidebarOpen={setSidebarOpen} newYearInterfaceEnabled={newYearInterfaceEnabled} />
            {/* Body: sidebar + content side by side on lg+ */}
            <div className="flex min-h-0 flex-1 overflow-hidden">
            {/* Sidebar */}
            <Sidebar
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              currentUser={currentUser}
              userRoles={userRoles}
              filteredMenuItems={filteredMenuItems}
              onLogout={handleLogout}
              newYearInterfaceEnabled={newYearInterfaceEnabled}
              animationsEnabled={animationsEnabled}
            />
            {/* Main Content */}
            <RouteScrollContainer>
              <AppErrorBoundary resetKey={window.location.hash}>
                <Routes>
                  <Route path="/" element={<MyOrders />} />
                  <Route path="/auth" element={<Navigate to="/" replace />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/profile" element={<Profile currentUser={currentUser} userRoles={userRoles} />} />
                  <Route path="/new-order" element={<NewOrder />} />
                  <Route path="/my-orders" element={<MyOrders />} />
                  <Route path="/tasks" element={canAccessTasks ? <TasksStagePage /> : <Navigate to="/my-orders" replace />} />
                  <Route path="/tasks/new" element={canAccessTasks ? <NewTaskPage /> : <Navigate to="/my-orders" replace />} />
                  <Route path="/tasks/:id" element={canAccessTasks ? <TaskDetail /> : <Navigate to="/my-orders" replace />} />
                  <Route path="/risk-control" element={canAccessRiskControl ? <TodayDashboard /> : <Navigate to="/my-orders" replace />} />
                  <Route path="/today" element={<Navigate to={canAccessRiskControl ? "/risk-control" : "/my-orders"} replace />} />
                  <Route path="/routes" element={<RoutesPage />} />
                  <Route path="/orders-log" element={canAccessOrdersLog ? <OrderLog /> : <Navigate to="/my-orders" replace />} />
                  <Route path="/order/:id/summary" element={<OrderCreatedSummaryPage />} />
                  <Route path="/order/:id" element={<OrderDetailWrapper canAccessOrdersLog={canAccessOrdersLog} />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/expenses/analytics" element={<ExpensesAnalytics />} />
                  <Route path="/statistics" element={<Statistics />} />
                  <Route path="/news" element={canAccessNews ? <News /> : <Navigate to="/my-orders" replace />} />
                  <Route path="/management" element={<Management userType={userType} />} />
                  <Route path="/management/users" element={<UsersManagement />} />
                  <Route path="/management/departments" element={<DepartmentsManagement />} />
                  <Route path="/management/services" element={<ServicesManagement />} />
                  <Route path="/management/client-app" element={userType === 'developer' ? <ClientAppManagement /> : <Navigate to="/management" replace />} />
                  <Route path="/management/clients" element={<ClientsManagement />} />
                  <Route path="/management/profit-radar" element={userType === 'developer' ? <ProfitRadar /> : <Navigate to="/management" replace />} />
                  <Route path="/management/activity" element={userType === 'developer' ? <ActivityLog /> : <Navigate to="/management" replace />} />
                  <Route path="/management/discounts" element={<DiscountsManagement />} />
                  <Route path="/management/notifications" element={<NotificationsManagement />} />
                  <Route path="/management/news" element={userType === 'developer' ? <NewsManagement /> : <Navigate to="/management" replace />} />
                  <Route path="/management/system" element={(userType === 'developer' || userType === 'admin') ? <SystemManagement /> : <Navigate to="/management" replace />} />
                  <Route path="/management/security" element={userType === 'developer' ? <SecurityManagement /> : <Navigate to="/management" replace />} />
                  <Route
                    path="/settings"
                    element={(
                      <Settings
                        theme={theme}
                        setTheme={setTheme}
                        userType={userType}
                        openChangePassword={window.location.hash === '#change-password'}
                      />
                    )}
                  />
                  <Route path="/user-agreement" element={<UserAgreementPage />} />
                  <Route path="*" element={<NotFoundRouteScreen authenticated />} />
                </Routes>
              </AppErrorBoundary>
            </RouteScrollContainer>
            </div>{/* end body flex row */}

            {/* PWA Install Button */}
            <PWAInstallButton />
            <NewsStartupModal
              enabled={(!!token && userAgreementAllowsAppEffects) || pwaUpdatePreviewEnabled}
              previewMode={pwaUpdatePreviewEnabled}
              canSkipRequiredUpdates={normalizedUserType === 'developer'}
            />
          </div>
          <OfflineModal />
          </OfflineProvider>
        </Router>
      </StageProvider>
    );
  }

  // Состояние загрузки (показываем только при проверке токена)
  return (
    <AppShellSkeleton />
  );
}

function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  currentUser,
  userRoles,
  filteredMenuItems,
  onLogout,
  newYearInterfaceEnabled = false,
  animationsEnabled = true
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateStage } = useStageContext();
  const [gestureProgress, setGestureProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [unreadNewsCount, setUnreadNewsCount] = useState(0);
  const startX = useRef(0);
  const dragStartedOnOverlay = useRef(false);
  const dragDistance = useRef(0);

  useModalBackButton(showLogoutModal, () => setShowLogoutModal(false));
  const currentX = useRef(0);
  const sidebarRef = useRef(null);
  const sidebarWidth = Math.min(window.innerWidth, 320); // 20rem = 320px
  const hasNewsMenuAccess = filteredMenuItems.some((item) => item.id === 'news');

  const refreshUnreadNewsCount = useCallback(async () => {
    if (!hasNewsMenuAccess) {
      setUnreadNewsCount(0);
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    try {
      const data = await getNewsUnreadCount();
      setUnreadNewsCount(Math.max(0, Number(data?.count) || 0));
    } catch (error) {
      console.warn('Не удалось обновить счётчик новостей:', error);
    }
  }, [hasNewsMenuAccess]);

  useEffect(() => {
    refreshUnreadNewsCount();
  }, [location.pathname, location.search, refreshUnreadNewsCount]);

  useEffect(() => {
    const handleFocus = () => refreshUnreadNewsCount();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUnreadNewsCount();
      }
    };
    const intervalId = window.setInterval(refreshUnreadNewsCount, 10 * 60 * 1000);

    window.addEventListener('focus', handleFocus);
    window.addEventListener(NEWS_STATE_CHANGED_EVENT, refreshUnreadNewsCount);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener(NEWS_STATE_CHANGED_EVENT, refreshUnreadNewsCount);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshUnreadNewsCount]);

  const navigateWithScrollReset = (target, options = {}) => {
    resetRouteScrollPosition();
    navigate(target, options);

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        resetRouteScrollPosition();
      });
      window.setTimeout(() => {
        resetRouteScrollPosition();
      }, 120);
    }
  };

  // Функция для проверки наличия открытых модальных окон
  const isModalOpen = () => {
    return parseInt(document.body.dataset.modalCount || '0', 10) > 0;
  };
  
  // Функция для генерации аватарки с двумя буквами
  const getUserAvatar = (firstName, lastName) => {
    const firstLetter = firstName?.[0]?.toUpperCase() || '';
    const lastLetter = lastName?.[0]?.toUpperCase() || '';
    return firstLetter + lastLetter || 'U';
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setGestureProgress(0);
    dragStartedOnOverlay.current = false;
    dragDistance.current = 0;
  };

  const startSidebarDrag = (clientX, { fromOverlay = false } = {}) => {
    if (isModalOpen()) return;
    setIsDragging(true);
    startX.current = clientX;
    currentX.current = clientX;
    dragStartedOnOverlay.current = fromOverlay;
    dragDistance.current = 0;
  };
  
  // Завершение перетаскивания
  const finishDrag = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Порог 90% - если прогресс >= 0.9, открываем полностью
    if (gestureProgress >= 0.9) {
      setSidebarOpen(true);
      setGestureProgress(1);
    } else {
      setSidebarOpen(false);
      setGestureProgress(0);
    }
  };
  
  // Добавляем глобальные обработчики для document
  useEffect(() => {
    const handleDocumentTouchStart = (e) => {
      // Проверяем открыта ли модалка в момент касания
      if (isModalOpen()) return;

      // Игнорируем тач по интерактивным элементам, чтобы обычный tap/click
      // не конфликтовал со свайпом бокового меню.
      const target = e.target;
      const touchX = e.touches[0].clientX;
      const sidebarRect = sidebarRef.current?.getBoundingClientRect?.() || null;
      const touchInsideSidebar = !!(sidebarRect && touchX >= sidebarRect.left && touchX <= sidebarRect.right);
      const touchNearSidebarEdge = !!(sidebarRect && touchInsideSidebar && (sidebarRect.right - touchX) <= 36);
      const isInteractive = target.closest('button') || target.closest('a');
      const isOverlay = !!target.closest('.sidebar-overlay');

      // На затемнении разрешаем полноценный свайп закрытия из любой точки,
      // чтобы не приходилось "ловить" край меню пальцем.
      if (sidebarOpen && isOverlay) {
        startSidebarDrag(touchX, { fromOverlay: true });
        return;
      }

      if (isInteractive && !(sidebarOpen && touchNearSidebarEdge)) {
        return;
      }

      // Разрешаем свайп только если меню открыто или касание у левого края (< 30px)
      if (sidebarOpen || e.touches[0].clientX < 30) {
        startSidebarDrag(e.touches[0].clientX);
      }
    };
    
    const handleDocumentTouchMove = (e) => {
      if (!isDragging) return;
      
      e.preventDefault(); // Предотвращаем скролл во время свайпа
      currentX.current = e.touches[0].clientX;
      dragDistance.current = Math.abs(currentX.current - startX.current);
      const deltaX = currentX.current - startX.current;
      
      // Вычисляем прогресс открытия (0 to 1)
      let progress;
      if (sidebarOpen) {
        // Если меню открыто, свайп ВЛЕВО (отрицательный deltaX) уменьшает прогресс
        // deltaX < 0 при движении влево, значит 1 + (negative) = меньше 1
        progress = Math.max(0, Math.min(1, 1 + (deltaX / sidebarWidth)));
      } else {
        // Если меню закрыто, свайп ВПРАВО (положительный deltaX) увеличивает прогресс
        progress = Math.max(0, Math.min(1, deltaX / sidebarWidth));
      }
      
      setGestureProgress(progress);
    };
    
    const handleDocumentTouchEnd = () => {
      if (!isDragging) return;

      const shouldCloseByOverlayTap = dragStartedOnOverlay.current && dragDistance.current < 8;
      
      // Сначала выключаем dragging (включает transition)
      setIsDragging(false);

      if (shouldCloseByOverlayTap) {
        haptic('close');
        closeSidebar();
        return;
      }

      dragStartedOnOverlay.current = false;
      dragDistance.current = 0;
      
      // Затем анимируем к конечной позиции
      if (gestureProgress >= 0.9) {
        setSidebarOpen(true);
        // Анимация от текущего progress до 1
        requestAnimationFrame(() => {
          setGestureProgress(1);
        });
      } else {
        setSidebarOpen(false);
        // Анимация от текущего progress до 0
        requestAnimationFrame(() => {
          setGestureProgress(0);
        });
      }
    };
    
    document.addEventListener('touchstart', handleDocumentTouchStart, { passive: true });
    document.addEventListener('touchmove', handleDocumentTouchMove, { passive: false });
    document.addEventListener('touchend', handleDocumentTouchEnd);
    
    return () => {
      document.removeEventListener('touchstart', handleDocumentTouchStart);
      document.removeEventListener('touchmove', handleDocumentTouchMove);
      document.removeEventListener('touchend', handleDocumentTouchEnd);
    };
  }, [isDragging, sidebarOpen, gestureProgress]);
  
  // Синхронизируем gestureProgress когда sidebarOpen меняется
  useEffect(() => {
    if (sidebarOpen) {
      setGestureProgress(1);
    } else {
      setGestureProgress(0);
    }
  }, [sidebarOpen]);

  // Защита от рассинхронизации после touch/click гонки:
  // состояние уже открыто, но transform мог остаться закрытым.
  useEffect(() => {
    if (sidebarOpen && !isDragging && gestureProgress === 0) {
      setGestureProgress(1);
    }
  }, [sidebarOpen, isDragging, gestureProgress]);
  
  // Вычисляем текущий transform и opacity на основе gestureProgress
  const translateX = `translateX(${(gestureProgress - 1) * 100}%)`;
  const overlayOpacity = gestureProgress * 0.5;
  
  return (
    <>
      {/* Swipe zone — only on mobile (hidden on lg) */}
      {!sidebarOpen && (
        <div
          className="fixed inset-y-0 left-0 z-30 w-5 bg-transparent lg:hidden"
          style={{ touchAction: 'pan-y' }}
          onTouchStart={(event) => {
            const touch = event.touches?.[0];
            if (!touch) return;
            startSidebarDrag(touch.clientX);
          }}
        />
      )}

      {/* Overlay — mobile only */}
      {gestureProgress > 0 && (
        <div
          className="sidebar-overlay fixed inset-0 bg-black z-40 lg:hidden"
          style={{
            opacity: overlayOpacity,
            transition: isDragging || !animationsEnabled ? 'none' : 'opacity 0.35s ease-out'
          }}
          onClick={() => {
            haptic('close');
            setSidebarOpen(false);
            setGestureProgress(0);
          }}
        />
      )}
      
      {/* Sidebar — mobile: fixed overlay drawer; lg+: static permanent sidebar */}
      <div
        ref={sidebarRef}
        className={`
          fixed left-0 top-0 bottom-0 z-50 flex min-h-0 w-[min(100vw,20rem)] max-w-full flex-col bg-white shadow-xl dark:bg-gray-900
          lg:static lg:z-auto lg:flex lg:h-auto lg:w-64 lg:shadow-none lg:border-r lg:border-gray-200 lg:dark:border-gray-800 lg-sidebar-static
          ${newYearInterfaceEnabled ? 'overflow-hidden' : ''}
        `}
        style={{ 
          height: '100dvh', 
          maxHeight: '100dvh', 
          paddingTop: 'env(safe-area-inset-top, 0px)',
          transform: translateX,
          transition: isDragging || !animationsEnabled ? 'none' : 'transform 0.35s ease-out'
        }}
      >
        
        <div className="relative z-10 shrink-0 border-b border-gray-200 px-4 pt-3 pb-3 dark:border-gray-800 lg:px-3 lg:py-2.5">
          <div className="new-year-sidebar-brand-row relative z-10 mb-2.5 flex items-center justify-between lg:mb-2">
            <NewYearSidebarBrand enabled={newYearInterfaceEnabled} />
            <button 
              onClick={(e) => {
                e.stopPropagation();
                haptic('close');
                setSidebarOpen(false);
                setGestureProgress(0);
              }} 
              className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden"
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-300" />
            </button>
          </div>
          <button
            onClick={() => {
              if (location.pathname === '/profile') {
                haptic('close');
                closeSidebar();
                return;
              }
              haptic('selection');
              clearRoutesNavigationContext();
              sessionStorage.removeItem('myOrdersBackStageStrict');
              navigateWithScrollReset('/profile', {
                replace: true,
                state: {
                  rootEntry: true,
                  rootSection: '/profile'
                }
              });
              closeSidebar();
            }}
            className="flex w-full items-center space-x-3 rounded-lg p-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 lg:space-x-2.5 lg:p-1.5"
          >
            <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-sm font-bold text-white lg:h-9 lg:w-9 lg:rounded-lg">
              {newYearInterfaceEnabled && <NewYearAvatarSantaHat />}
              {getUserAvatar(currentUser.first_name, currentUser.last_name)}
            </div>
            <div className="min-w-0 flex-1 text-left">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{currentUser.name}</span>
                {newYearInterfaceEnabled && <NewYearTreeButton />}
              </div>
              <div className="truncate text-xs text-gray-600 dark:text-gray-300">{userRoles[currentUser.role]}</div>
            </div>
          </button>
        </div>
        <nav className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-4 pt-3 [-webkit-overflow-scrolling:touch] scrollbar-hide lg:p-3 lg:pt-2">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (item.path === '/my-orders' && location.pathname === '/');
            return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (isActive) {
                      haptic('close');
                      closeSidebar();
                      return;
                    }
                    haptic('selection');
                    clearRoutesNavigationContext();
                    sessionStorage.removeItem('myOrdersBackStageStrict');
                    if (item.path === '/my-orders') {
                      sessionStorage.removeItem('myOrdersStage');
                      sessionStorage.removeItem('myOrdersActiveTab');
                      updateStage(null);
                    }
                    navigateWithScrollReset(item.path, {
                      replace: true,
                      state: {
                        rootEntry: true,
                        rootSection: item.path
                      }
                    });
                    closeSidebar();
                  }}
                className={`mb-1.5 flex w-full items-center space-x-3 rounded-xl px-3 py-2.5 transition-all last:mb-0 sm:px-4 sm:py-3 lg:mb-1 lg:space-x-2.5 lg:px-3 lg:py-2 lg:rounded-lg
                  ${isActive
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'}
                `}
              >
                <span className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center">
                  <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-gray-700 dark:text-gray-200'}`} />
                  {item.id === 'news' && unreadNewsCount > 0 ? (
                    <span
                      className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-900"
                      title={`Непрочитанных новостей: ${unreadNewsCount}`}
                      aria-label={`Непрочитанных новостей: ${unreadNewsCount}`}
                    />
                  ) : null}
                </span>
                <span className="text-left text-sm font-medium sm:text-base lg:text-sm">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div
          className="shrink-0 border-t border-gray-200 p-4 pt-3 dark:border-gray-800 lg:p-3 lg:pt-2"
          style={{ paddingBottom: `max(0.75rem, ${APP_BOTTOM_SAFE_OFFSET})` }}
        >
          <button
            onClick={() => {
              haptic('warning');
              setShowLogoutModal(true);
            }}
            className="flex w-full items-center space-x-3 rounded-xl px-3 py-2.5 text-red-600 transition-all hover:bg-red-50 dark:hover:bg-gray-800 sm:px-4 sm:py-3 lg:space-x-2.5 lg:px-3 lg:py-2 lg:rounded-lg"
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            <span className="font-medium text-sm sm:text-base lg:text-sm">Выйти</span>
          </button>
        </div>
      </div>

      {/* Модалка подтверждения выхода */}
      <ModalShell
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        variant="window"
        panelClassName="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900"
      >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <LogOut className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Выход из системы</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Вы уверены, что хотите выйти?</p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  haptic('close');
                  setShowLogoutModal(false);
                }}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium text-sm"
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  haptic('heavy');
                  setShowLogoutModal(false);
                  setSidebarOpen(false);
                  onLogout();
                }}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium text-sm flex items-center justify-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Выйти
              </button>
            </div>
          </ModalShell>
    </>
  );
}

export default App;
